// Workspaces + staff impersonation client.
//
// Workspaces are the multi-tenant container the active-workspace switch
// keys off. Staff (Railyard team) is the admin-on-tenants surface — it
// rides along because the impersonation flow operates on workspace
// identity. Mirrors src/railyard/routes/workspaces.py + routes/staff.py.

import type { components } from "@/lib/api-types";
import { logger } from "@/lib/log";

import {
  API,
  apiFetch,
  authScopeKey,
  clearActiveWorkspacePin,
  hasLiveImpersonation,
  notifyTenantContextChanged,
  readActiveWorkspacePin,
  requestJson,
  requestOk,
  setActiveWorkspacePin,
} from "../api";
import { updateDefaultWorkspace } from "./org";


type Schemas = components["schemas"];


const log = logger("api/workspaces");


// ---------------------------------------------------------------------------
// Staff (Railyard team) — impersonation
// ---------------------------------------------------------------------------

export type StaffTarget = Schemas["StaffTarget"];
export type StaffImpersonateResponse = Schemas["StaffImpersonateResponse"];

export async function listStaffTargets(q?: string): Promise<StaffTarget[]> {
  const qs = q ? `?q=${encodeURIComponent(q)}` : "";
  return requestJson(`${API}/api/staff/targets${qs}`);
}

export async function startImpersonation(target_uid: string): Promise<StaffImpersonateResponse> {
  return requestJson(`${API}/api/staff/impersonate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target_uid }),
  });
}

export async function endImpersonation(): Promise<void> {
  // Best-effort: ask the server to log the close event before we drop the
  // token client-side. If the call fails, we still clear locally.
  try {
    await apiFetch(`${API}/api/staff/impersonate/end`, { method: "POST" });
  } catch (e) {
    log.warn("endImpersonation: server-side close failed (cleared locally)", e);
  }
}


// ---------------------------------------------------------------------------
// Staff (Railyard team) — admin dashboard: feature flags + credit grants
//
// Cross-tenant admin surface. Unlike the per-tenant admin PATCH
// /api/workspace/settings, these operate on ANY workspace and are gated on
// `is_staff`. Mirrors routes/staff.py.
// ---------------------------------------------------------------------------

export type StaffFeatureSpec = Schemas["StaffFeatureSpec"];
export type StaffWorkspaceSummary = Schemas["StaffWorkspaceSummary"];
export type StaffWorkspaceDetail = Schemas["StaffWorkspaceDetail"];
export type StaffDuplicateCandidate = Schemas["StaffDuplicateCandidate"];
export type StaffDuplicateCheckResponse = Schemas["StaffDuplicateCheckResponse"];
export type StaffGrantCreditsResponse = Schemas["StaffGrantCreditsResponse"];
export type StaffEnrollResponse = Schemas["StaffEnrollResponse"];

export async function listStaffFeatures(): Promise<StaffFeatureSpec[]> {
  return requestJson(`${API}/api/staff/features`);
}

export async function listStaffWorkspaces(
  q?: string,
): Promise<StaffWorkspaceSummary[]> {
  const qs = q ? `?q=${encodeURIComponent(q)}` : "";
  return requestJson(`${API}/api/staff/workspaces${qs}`);
}

export async function checkStaffWorkspaceDuplicates(params: {
  tenant_id: string;
  display_name?: string;
}): Promise<StaffDuplicateCheckResponse> {
  const qs = new URLSearchParams({ tenant_id: params.tenant_id });
  if (params.display_name) qs.set("display_name", params.display_name);
  return requestJson(`${API}/api/staff/duplicate-workspace-check?${qs}`);
}

export async function getStaffWorkspace(
  tenantId: string,
): Promise<StaffWorkspaceDetail> {
  return requestJson(
    `${API}/api/staff/workspaces/${encodeURIComponent(tenantId)}`,
  );
}

export async function setStaffWorkspaceFeatures(
  tenantId: string,
  betaFeatures: string[],
): Promise<StaffWorkspaceDetail> {
  return requestJson(
    `${API}/api/staff/workspaces/${encodeURIComponent(tenantId)}/features`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ beta_features: betaFeatures }),
    },
  );
}

export async function grantStaffWorkspaceCredits(
  tenantId: string,
  body: {
    amount_usd: number;
    name?: string;
    note?: string;
    expires_in_days?: number;
  },
): Promise<StaffGrantCreditsResponse> {
  return requestJson(
    `${API}/api/staff/workspaces/${encodeURIComponent(tenantId)}/credits/grant`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export type StaffLedgerEntry = Schemas["StaffLedgerEntry"];
export type StaffLedgerResponse = Schemas["StaffLedgerResponse"];

// Newest-first credit ledger history for a workspace. `limit` defaults to 50
// server-side, clamped to [1, 200].
export async function getStaffWorkspaceLedger(
  tenantId: string,
  limit?: number,
): Promise<StaffLedgerResponse> {
  const qs = limit !== undefined ? `?limit=${encodeURIComponent(String(limit))}` : "";
  return requestJson(
    `${API}/api/staff/workspaces/${encodeURIComponent(tenantId)}/credits/ledger${qs}`,
  );
}

// Parallel-run: enroll a grandfathered OG subscriber into new-gen (explicit IDs).
// The target railyard workspace is `target_org_id` (in the body, not the path);
// the server reads the OG subscription to derive plan/seats/period. Mirrors
// routes/billing.py::staff_enroll.
export async function staffEnrollWorkspace(body: {
  target_org_id: string;
  og_organization_id: string;
  stripe_subscription_id: string;
  stripe_customer_id?: string;
}): Promise<StaffEnrollResponse> {
  return requestJson(`${API}/api/staff/enroll`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}


// ---------------------------------------------------------------------------
// Workspaces
// ---------------------------------------------------------------------------

export type WorkspaceInfo = Schemas["WorkspaceInfo"];
export type WorkspaceMember = Schemas["WorkspaceMember"];
export type WorkspaceInvite = Schemas["WorkspaceInvite"];
export type WorkspaceInvitePreview = Schemas["WorkspaceInvitePreview"];
export type AcceptWorkspaceInviteResponse = Schemas["AcceptWorkspaceInviteResponse"];

// The workspace list is small, changes rarely, and is read on every mount of
// the workspace switcher — including on mobile, where `MobileDrawer` unmounts
// the whole sidebar subtree when the nav drawer closes (mobile-shell.tsx), so
// every hamburger tap used to fire a fresh request and flash a skeleton in its
// place (ENG-5257). Hold the last response in memory so a remount renders from
// it immediately and revalidates behind the UI.
//
// Keyed by `authScopeKey()` — the user / active-workspace / impersonation
// triple the backend varies this response on — so a cache entry can never be
// served to a different principal than the one it was fetched for. A key
// mismatch is a miss, not a stale read.
let _workspacesCache: { key: string; value: WorkspaceInfo[]; at: number } | null = null;
// In-flight de-dupe: the switcher and the workspace-settings panel can mount
// in the same tick and would otherwise issue two identical requests.
let _workspacesInFlight: { key: string; promise: Promise<WorkspaceInfo[]> } | null = null;
// Bumped by every invalidation. A request that was already in flight when the
// cache was dropped must not re-publish what it fetched *before* the mutation
// that dropped it — the create-then-late-response race.
let _workspacesGeneration = 0;

/**
 * The cached workspace list for the current principal, or null when nothing
 * has been fetched for it yet.
 *
 * For render-time seeding only: callers show this immediately and still call
 * `listWorkspaces()` to revalidate. It never issues a request itself, so it is
 * safe to read during render.
 */
export function getCachedWorkspaces(): WorkspaceInfo[] | null {
  if (_workspacesCache === null) return null;
  return _workspacesCache.key === authScopeKey() ? _workspacesCache.value : null;
}

/**
 * Drop the cached list. Call after a mutation that changes which workspaces the
 * user has — today, creating one.
 */
export function invalidateWorkspacesCache(): void {
  _workspacesCache = null;
  _workspacesInFlight = null;
  _workspacesGeneration++;
}

/**
 * Fetch the user's workspaces and refresh the cache with the result.
 *
 * `maxAgeMs` lets a caller accept a recent cached list instead of revalidating:
 * without it every remount still costs a request, which on mobile is one per
 * hamburger tap. The membership list changes on invite-accept and create — the
 * latter invalidates here, the former is a different session — so a short
 * window trades nothing real for that traffic.
 */
export async function listWorkspaces(
  opts?: { maxAgeMs?: number },
): Promise<WorkspaceInfo[]> {
  const key = authScopeKey();
  const generation = _workspacesGeneration;
  const maxAgeMs = opts?.maxAgeMs;
  if (
    maxAgeMs != null &&
    _workspacesCache?.key === key &&
    Date.now() - _workspacesCache.at < maxAgeMs
  ) {
    return _workspacesCache.value;
  }
  if (_workspacesInFlight?.key === key) return _workspacesInFlight.promise;
  const promise = (async () => {
    const value: WorkspaceInfo[] = await requestJson(`${API}/api/workspaces`);
    // Only publish under the key the request went out with, and only if the
    // cache hasn't been invalidated meanwhile: a principal switch mid-flight
    // (impersonation start/stop, workspace pin change) must not land the old
    // principal's list under the new one's key, and a response that predates a
    // create must not come back as the current list.
    if (authScopeKey() === key && _workspacesGeneration === generation) {
      _workspacesCache = { key, value, at: Date.now() };
    }
    return value;
  })();
  _workspacesInFlight = { key, promise };
  try {
    return await promise;
  } finally {
    if (_workspacesInFlight?.promise === promise) _workspacesInFlight = null;
  }
}

export async function createWorkspace(body: {
  tenant_id: string;
  display_name: string;
  agent_persona?: string;
}): Promise<Schemas["WorkspaceCreateResponse"]> {
  const res: Schemas["WorkspaceCreateResponse"] = await requestJson(
    `${API}/api/workspaces`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  // The user now has a workspace the cached list doesn't know about.
  invalidateWorkspacesCache();
  return res;
}

export async function updateWorkspaceProfile(
  tenantId: string,
  updates: { display_name?: string; agent_persona?: string }
): Promise<void> {
  await requestOk(
    `${API}/api/workspaces/${encodeURIComponent(tenantId)}/profile`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    },
  );
}

export async function listWorkspaceMembers(
  tenantId: string
): Promise<WorkspaceMember[]> {
  return requestJson(
    `${API}/api/workspaces/${encodeURIComponent(tenantId)}/members`,
  );
}

// The single role a teammate can be invited at. `owner` is a home-org concept
// granted only by post-join promotion, never by invite.
export type InviteRole = "member" | "admin";

export type BulkInviteResult = Schemas["BulkInviteResult"];

// Invite a user by email to join a workspace. The backend emails the recipient
// an accept link. Returns the full invite, including the token so the admin can
// copy the accept URL too. This is the one invite path since ENG-1874 (it backs
// the Members panel's "Invite member" for the active workspace, home or not).
export async function createWorkspaceInvite(
  tenantId: string,
  email: string,
  role: InviteRole = "member",
): Promise<WorkspaceInvite> {
  return requestJson(
    `${API}/api/workspaces/${encodeURIComponent(tenantId)}/invites`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, role }),
    },
  );
}

// Mint up to 50 invites from a comma/newline-separated paste. Partial success:
// returns { created, skipped: [{ email, reason }] }.
export async function createWorkspaceInvitesBulk(
  tenantId: string,
  emails: string,
  role: InviteRole = "member",
): Promise<BulkInviteResult> {
  return requestJson(
    `${API}/api/workspaces/${encodeURIComponent(tenantId)}/invites/bulk`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ emails, role }),
    },
  );
}

export async function listWorkspaceInvites(
  tenantId: string,
): Promise<WorkspaceInvite[]> {
  return requestJson(
    `${API}/api/workspaces/${encodeURIComponent(tenantId)}/invites`,
  );
}

export async function revokeWorkspaceInvite(
  tenantId: string,
  token: string,
): Promise<void> {
  await requestOk(
    `${API}/api/workspaces/${encodeURIComponent(tenantId)}/invites/${encodeURIComponent(token)}`,
    { method: "DELETE" },
  );
}

export async function resendWorkspaceInvite(
  tenantId: string,
  token: string,
): Promise<void> {
  await requestOk(
    `${API}/api/workspaces/${encodeURIComponent(tenantId)}/invites/${encodeURIComponent(token)}/resend`,
    { method: "POST" },
  );
}

// Public preview for the accept page (unauthenticated).
export async function previewWorkspaceInvite(
  token: string,
): Promise<WorkspaceInvitePreview> {
  return requestJson(
    `${API}/api/workspace-invites/${encodeURIComponent(token)}`,
  );
}

// Redeem a workspace invite as the signed-in (already-bootstrapped) user.
export async function acceptWorkspaceInvite(
  token: string,
): Promise<AcceptWorkspaceInviteResponse> {
  return requestJson(`${API}/api/me/accept-workspace-invite`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
}

// ---------------------------------------------------------------------------
// Join requests (ENG-2509 Phase 1 admin-approve)
//
// A domain-matched signup's "request to join <Firm>" (Phase 0b). Approve
// grants `member` workspace membership directly for the requester; decline
// just resolves the request with no grant. Mirrors routes/workspaces.py.
// ---------------------------------------------------------------------------

export type JoinRequestInfo = Schemas["JoinRequestInfo"];

export async function listJoinRequests(tenantId: string): Promise<JoinRequestInfo[]> {
  return requestJson(
    `${API}/api/workspaces/${encodeURIComponent(tenantId)}/join-requests`,
  );
}

export async function approveJoinRequest(
  tenantId: string,
  uid: string,
): Promise<JoinRequestInfo> {
  return requestJson(
    `${API}/api/workspaces/${encodeURIComponent(tenantId)}/join-requests/${encodeURIComponent(uid)}/approve`,
    { method: "POST" },
  );
}

export async function declineJoinRequest(
  tenantId: string,
  uid: string,
): Promise<JoinRequestInfo> {
  return requestJson(
    `${API}/api/workspaces/${encodeURIComponent(tenantId)}/join-requests/${encodeURIComponent(uid)}/decline`,
    { method: "POST" },
  );
}

export async function removeWorkspaceMember(
  tenantId: string,
  userId: string
): Promise<void> {
  await requestOk(
    `${API}/api/workspaces/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

export function getActiveWorkspaceTenantId(): string | null {
  // Delegates so every reader sees the same pin the wire does — in particular,
  // an impersonation-stamped pin whose session has ended reads as absent
  // (ENG-4155).
  return readActiveWorkspacePin();
}

// Switch the active workspace to `tenantId` and land on the workspace admin
// (Customize) surface. Used by the staff console's "Open in admin view" link —
// staff are often members of the target workspace, so the active-workspace
// switch resolves it as themselves (no impersonation). If they're not a
// member, Customize will render its own not-authorized state.
export function goToWorkspaceAdmin(tenantId: string): void {
  if (typeof window === "undefined") return;
  setActiveWorkspacePin(tenantId);
  // Workspace ADMIN, explicitly. This used to be a bare "/customize", which
  // resolved to Settings → workspace back when /customize was the settings
  // route; after ENG-5264 that path is Customize → Skills, which is not the
  // admin view this button promises.
  window.location.assign("/settings/workspace");
}

// `opts.persist` (ENG-3037) mirrors the switch to the server as the caller's
// default-workspace preference before reloading — best-effort, and never
// allowed to block the local switch: a failed PATCH is swallowed (warned)
// and the workspace change proceeds exactly as it would without persist.
// Omitting `opts` (every caller before ENG-3037) keeps the function's
// synchronous-until-reload behavior byte-for-byte: with no `persist`, there's
// no `await` in the body, so the localStorage write + reload/assign still run
// synchronously within this call, only wrapped in an (immediately resolved)
// Promise.
export async function setActiveWorkspace(
  tenantId: string | null,
  opts?: { persist?: boolean },
): Promise<void> {
  if (typeof window === "undefined") return;
  if (opts?.persist) {
    try {
      await updateDefaultWorkspace(tenantId);
    } catch (e) {
      log.warn("updateDefaultWorkspace failed (switching locally anyway)", e);
    }
  }
  if (tenantId === null) {
    clearActiveWorkspacePin();
  } else {
    setActiveWorkspacePin(tenantId);
  }
  // While impersonating there is no reload available: it would end the session
  // and orphan the pin we just wrote, ejecting the operator to their own home
  // workspace instead of the one they picked (codex P2). Re-resolve in place
  // instead — the same mechanism the 403 recovery uses. A URL scoped to a
  // resource in the previous workspace will surface a normal not-found rather
  // than silently showing another tenant's data.
  if (hasLiveImpersonation()) {
    await notifyTenantContextChanged();
    return;
  }
  // Resources keyed by an ID (a task, project, or deliverable) don't exist in
  // the workspace we're switching into, so reloading the same URL lands on a
  // "not found" page. Send those to /new instead; everything else just reloads.
  const path = window.location.pathname;
  const isScopedResource =
    /^\/(task|project|deliverable)\//.test(path);
  if (isScopedResource) {
    window.location.assign("/new");
  } else {
    window.location.reload();
  }
}
